import { type ReactNode, useMemo } from 'react';

import type { RecipeWizardData } from '@/sections/asset/components/tabs/credentials/RecipeWizard/RecipeWizard.types';
import { wizardToRecipe } from '@/sections/asset/components/tabs/credentials/RecipeWizard/wizardConfig';

import { SectionIntro } from './shared';

interface ReviewStepProps {
  data: RecipeWizardData;
  webAppUrl: string;
}

export function ReviewStep({ data, webAppUrl }: ReviewStepProps) {
  const sessionDesc = useMemo(() => describeSession(data), [data]);
  const replayDesc = useMemo(() => describeReplay(data), [data]);

  return (
    <div className="space-y-5">
      <SectionIntro
        title="Review the recipe"
        body={
          <p>
            Here is what will happen on every scan against{' '}
            <span className="font-mono text-xs">
              {webAppUrl || 'this web application'}
            </span>
            . Use <strong>Back</strong> to fix anything, or{' '}
            <strong>Save Recipe</strong> to commit.
          </p>
        }
      />

      <ol className="space-y-3">
        <ReviewItem
          n={1}
          title="Log in"
          body={
            <>
              Send a <code>{data.method}</code> to{' '}
              <span className="font-mono text-xs">{data.url}</span>
              {data.contentType && (
                <>
                  {' '}
                  with Content-Type <code>{data.contentType}</code>
                </>
              )}
              {data.body && (
                <div className="bg-chariot-background-secondary mt-1 rounded p-2 font-mono text-xs">
                  {data.body}
                </div>
              )}
            </>
          }
        />
        <ReviewItem n={2} title="Capture the session" body={sessionDesc} />
        <ReviewItem n={3} title="Add to every scan request" body={replayDesc} />
      </ol>

      <details className="border-chariot-stroke rounded-md border p-3">
        <summary className="text-chariot-text-secondary cursor-pointer text-sm">
          Show raw recipe (advanced)
        </summary>
        <pre className="bg-chariot-background-secondary mt-2 overflow-x-auto rounded p-3 text-xs">
          {JSON.stringify(wizardToRecipe(data), null, 2)}
        </pre>
      </details>
    </div>
  );
}

// describeSession / describeReplay live in ReviewStep instead of in
// wizardConfig so the config file stays JSX-free (a .ts file). They each
// switch on sessionSource, but the *projection into wire format* — the
// part that used to drift — is centralized in SESSION_STRATEGIES.

function describeSession(d: RecipeWizardData): ReactNode {
  if (d.sessionSource === 'cookie') {
    return (
      <>
        Read the <code>{d.cookieName}</code> cookie from the response.
      </>
    );
  }
  if (d.sessionSource === 'response_header') {
    return (
      <>
        Read the <code>{d.headerName}</code> response header.
      </>
    );
  }
  return (
    <>
      Read <code>{d.jsonPath}</code> from the JSON response body.
    </>
  );
}

function describeReplay(d: RecipeWizardData): ReactNode {
  if (d.sessionSource === 'cookie') {
    return (
      <>
        Add <code>Cookie: {d.cookieName}=&lt;captured&gt;</code> to every scan
        request.
      </>
    );
  }
  if (d.sessionSource === 'response_header') {
    return (
      <>
        Add <code>{d.headerName}: &lt;captured&gt;</code> to every scan request.
      </>
    );
  }
  return (
    <>
      Add{' '}
      <code>
        {d.bodyOutputHeader}:{' '}
        {d.bodyOutputValue.replace('{{token}}', '<captured>')}
      </code>{' '}
      to every scan request.
    </>
  );
}

function ReviewItem({
  n,
  title,
  body,
}: {
  n: number;
  title: string;
  body: ReactNode;
}) {
  return (
    <li className="border-chariot-stroke flex gap-3 rounded-md border p-3">
      <div className="bg-chariot-brand-primary/10 text-chariot-brand-primary flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-bold">
        {n}
      </div>
      <div className="flex-1">
        <div className="text-chariot-text-primary text-sm font-medium">
          {title}
        </div>
        <div className="text-chariot-text-primary mt-1 text-sm">{body}</div>
      </div>
    </li>
  );
}
